import { states } from '../utils/helpers'
import { timeAgo } from '../utils/dateHelpers'
import { z } from 'zod'
import {
  CURRENT_AUTH_LETTER_VERSION,
  ProfileFields,
} from '../redux/profile/ProfileSlice'
import { phoneNumberSchema } from './util'

export const createAddressSchema = (isStreetOptional: boolean, isZipOptional: boolean = false) => {
  const addressSchema = z.object({
    city: z
      .string()
      .nonempty('City is required')
      .max(100, 'City cannot be longer than 100 characters'),
    state: z.enum(states, {
      errorMap: (issue, ctx) => {
        if (!ctx.data || ctx.data === '') {
        return { message: 'State is required' };
      }
        return { message: 'Invalid state code (e.g. NY)' };
      },
    }),
    streetAddress: z
      .string()
      .max(100, 'Street cannot be longer than 100 characters')
      .refine((value) => isStreetOptional || value.trim().length > 0, {
        message: 'Street is required',
      }),
    zip: isZipOptional
      ? z.string().regex(/^\d*$/, 'Zip code must contain only numbers').refine((val) => val.length === 0 || val.length === 5, { message: 'Zip code must contain 5 digits' })
      : z.string().nonempty('Zip is required').regex(/^\d*$/, 'Zip code must contain only numbers').refine((val) => val.length === 5, { message: 'Zip code must contain 5 digits' }),
  })
  return addressSchema.array()
}
const addressSchema = z.object({
  city: z
    .string()
    .nonempty('City is required')
    .max(100, 'City cannot be longer than 100 characters'),
  state: z.enum(states, {
    errorMap: () => ({
      message: 'Invalid state code (e.g. NY)',
    }),
  }),
  streetAddress: z
    .string()
    .max(100, 'Street cannot be longer than 100 characters'),
  zip: z
    .string()
    .regex(/^\d*$/, 'Zip code must contain only numbers')
    .max(10, 'Zip code cannot be longer than 10 characters'),
})

export const registerSchema = z.object({
  firstName: z
    .string()
    .nonempty('First name is required')
    .max(50, 'Name cannot be more than 50 characters')
    .regex(
      /^[a-zA-Z-]+$/,
      'First name can only contain letters and hyphens (-)',
    ),
  middleName: z
    .string()
    .max(50, 'Name cannot be more than 50 characters')
    .regex(
      /^[a-zA-Z-]+$/,
      'Middle name can only contain letters and hyphens (-)',
    ),
  lastName: z
    .string()
    .nonempty('Last name is required')
    .max(50, 'Name cannot be more than 50 characters')
    .regex(
      /^[a-zA-Z-]+$/,
      'Last name can only contain letters and hyphens (-)',
    ),
  city: z
    .string()
    .nonempty('City is required')
    .max(100, 'City cannot be longer than 100 characters'),
  state: z.enum(states, {
    errorMap: () => ({
      message: 'Invalid state code (e.g. NY)',
    }),
  }),
  streetAddress: z.string(),
  zip: z.string(),
  email: z.string().email().or(z.literal('')),
  phoneNumber: phoneNumberSchema,
  birthdate: z.coerce
    .date()
    .max(
      timeAgo({ years: 18 }),
      'You must be at least 18 years old to use Priwall',
    )
    .min(
      timeAgo({ years: 100 }),
      'You must be under 100 years old to use Priwall',
    )
    .nullable()
    .refine((val) => val !== null, {
      message: 'Please select a valid date of birth',
    }),
  linkedIn: z
    .string()
    .trim()
    .url('Invalid LinkedIn URL')
    .max(120, 'LinkedIn URL cannot exceed 120 characters')
    .regex(
      /^https:\/\/([a-z]{2,3}\.)?linkedin\.com\/.*$/,
      'LinkedIn URL must be a valid LinkedIn profile link',
    )
    .optional()
    .or(z.literal('')),
})

// Define a function to create the schema with dynamic `middleName` requirement
export const phoneNumberObjectSchema = (isPhoneNumberOptional: boolean) =>
  z.object({
    value: phoneNumberSchema.refine(
      (val) => isPhoneNumberOptional || val.length > 0,
      { message: 'Phone number is required' },
    ),
  })

export const createProfileSchema = (
  isLinkedinOptional: boolean,
  isStreetOptional: boolean,
  isPhoneNumberOptional: boolean,
  agreeToTermsOptional: boolean,
  isZipOptional: boolean = false,
) => {
  const profileSchema = z.object({
    firstName: z
      .object({
        value: z
          .string()
          .min(1, 'First name is required')
          .max(50, 'Name cannot be more than 50 characters')
          .regex(
            /^[a-zA-Z]+(?:[\s-][a-zA-Z]+)*$/,
            'First name can only contain letters and hyphens (-)',
          ),
      })
      .array(),

    middleName: z
      .object({
        value: z
          .string()
          .max(50, 'Name cannot be more than 50 characters')
          .regex(
            /^(?:[a-zA-Z]+(?:[\s-][a-zA-Z]+)*)?$/,
            'Middle name can only contain letters and hyphens (-)',
          )
          .refine((value) => value.length > 0, {
            message: 'Middle name is required',
          }),
      })
      .array(),
    lastName: z
      .object({
        value: z
          .string()
          .min(1, 'Last name is required')
          .max(50, 'Name cannot be more than 50 characters')
          .regex(
            /^[a-zA-Z]+(?:[\s-][a-zA-Z]+)*$/,
            'Last name can only contain letters and hyphens (-)',
          ),
      })
      .array(),

    otherFirstName: z
      .object({
        value: z
          .string()
          .max(50, 'Name cannot be more than 50 characters')
          .regex(
            /^$|^[a-zA-Z]+(?:[\s-][a-zA-Z]+)*$/,
            'First Name can only contain letters and hyphens (-)',
          )
          .optional(),
      })
      .array()
      .optional(),

    otherMaidenName: z
      .object({
        value: z
          .string()
          .max(50, 'Name cannot be more than 50 characters')
          .regex(
            /^$|^[a-zA-Z]+(?:[\s-][a-zA-Z]+)*$/,
            'Maiden name can only contain letters and hyphens (-)',
          )
          .optional(),
      })
      .array()
      .optional(),

    phoneNumber: isPhoneNumberOptional
      ? z.array(phoneNumberObjectSchema(isPhoneNumberOptional)).optional()
      : z.array(phoneNumberObjectSchema(isPhoneNumberOptional)),
    email: z
      .object({ value: z.string().email().max(100).or(z.literal('')) })
      .array(),
    birthdate: z.coerce
      .date()
      .nullable()
      .refine((date) => date !== null && !isNaN(date.getTime()), {
        message: 'Date of birth is required',
      })
      .refine((date) => date && date <= timeAgo({ years: 18 }), {
        message: 'You must be at least 18 years old to use Priwall',
      })
      .refine((date) => date && date >= timeAgo({ years: 100 }), {
        message: 'You must be under 100 years old to use Priwall',
      }),
    address: createAddressSchema(isStreetOptional, isZipOptional),
    mp_auth_letter_signature: z.string(),
    auth_letter_s3_key: z.string(),
    gender: z.string(),
    auth_letter_version: z.string(),
    linkedIn: isLinkedinOptional
      ? z
          .string()
          .max(120, 'LinkedIn URL cannot exceed 120 characters')
          .refine(
            (value) =>
              !value ||
              (/^https:\/\/([a-z]{2,3}\.)?linkedin\.com\/.*$/.test(value) && !/\s/.test(value)),
            {
              message:
                'Invalid LinkedIn URL. Please use the format: https://www.linkedin.com/your-profile',
            },
          )
          .optional()
      : z
          .string()
          .max(120, 'LinkedIn URL cannot exceed 120 characters')
          .refine((value) => value.length > 0, {
            message: 'LinkedIn URL is required',
          })
          .refine(
            (value) =>
              !value ||
              (/^https:\/\/([a-z]{2,3}\.)?linkedin\.com\/.*$/.test(value) && !/\s/.test(value)),
            {
              message:
                'Invalid LinkedIn URL. Please use the format: https://www.linkedin.com/your-profile',
            },
          ),
    agreeToTerms: agreeToTermsOptional
      ? z.boolean().refine((value) => value === true, {
          message: 'You must agree to the terms and conditions',
        })
      : z.boolean().optional(),
    tour_state: z.object({
      free_user_tour_state: z.string().nullable().optional(),
      subscribed_user_tour_state: z.string().nullable().optional(),
    })
    .optional(),
    last_scan_initiated_at: z.coerce
      .string()
      .nullable()
      .optional(),
    metadata: z.object({
      dob_counter: z.number().nullable().optional(),
      todo_actions: z.array(z.union([
        z.object({
          action_category: z.literal('broker'),
          broker_name: z.string(),
          action: z.enum(['removal', 'confirmator', 'later']),
        }),
        z.object({
          action_category: z.literal('data_comm_acknowledgement'),
          action: z.enum(['acknowledge', 'later']),
          completed_at: z.string().nullable().optional(),
        })
      ])).optional().default([]),
      getting_started_completed: z.boolean().nullable().optional(),
      onboarding_step: z.number().nullable().optional(),
    }).optional(),
    created_at: z.string().nullable().optional(),
  })

  return profileSchema
}

export const profileSchema = z.object({
  firstName: z
    .object({
      value: z
        .string()
        .nonempty('First name is required')
        .max(50, 'Name cannot be more than 50 characters')
        .regex(
          /^[a-zA-Z]+(?:[\s-][a-zA-Z]+)*$/,
          'First name can only contain letters and hyphens (-)',
        ),
    })
    .array(),
  middleName: z
    .object({
      value: z
        .string()
        .max(50, 'Name cannot be more than 50 characters')
        .regex(
          /^(?:[a-zA-Z]+(?:[\s-][a-zA-Z]+)*)?$/,
          'Middle name can only contain letters and hyphens (-)',
        )
        .refine((value) => !(value || /^[a-zA-Z-]+$/.test(value)), {
          message: 'Middle name can only contain letters and hyphens (-)',
        }),
    })
    .array(),
  lastName: z
    .object({
      value: z
        .string()
        .max(50, 'Name cannot be more than 50 characters')
        .nonempty('Last name is required')
        .regex(
          /^[a-zA-Z]+(?:[\s-][a-zA-Z]+)*$/,
          'Last name can only contain letters and hyphens (-)',
        ),
    })
    .array(),
  otherFirstName: z
    .object({
      value: z
        .string()
        .max(50, 'Name cannot be more than 50 characters')
        .regex(
          /^$|^[a-zA-Z]+(?:[\s-][a-zA-Z]+)*$/,
          'First name can only contain letters and hyphens (-)',
        )
        .optional(),
    })
    .array()
    .optional(),
  otherMaidenName: z
    .object({
      value: z
        .string()
        .max(50, 'Name cannot be more than 50 characters')
        .regex(
          /^$|^[a-zA-Z]+(?:[\s-][a-zA-Z]+)*$/,
          'Maiden name can only contain letters and hyphens (-)',
        )
        .optional(),
    })
    .array()
    .optional(),
  phoneNumber: z
    .object({
      value: phoneNumberSchema,
    })
    .array(),
  email: z
    .object({
      value: z.string().email().max(100).or(z.literal('')),
    })
    .array(),
  birthdate: z.coerce
    .date()
    .max(
      timeAgo({ years: 18 }),
      'You must be at least 18 years old to use Priwall',
    )
    .min(
      timeAgo({ years: 100 }),
      'You must be under 100 years old to use Priwall',
    )
    .nullable()
    .refine((val) => val !== null, {
      message: 'Please select a valid date of birth',
    }),
  address: addressSchema.array(),
  mp_auth_letter_signature: z.string(),
  auth_letter_s3_key: z.string(),
  gender: z.string(),
  auth_letter_version: z.string(),
  linkedIn: z
    .string()
    .trim()
    .url('Invalid LinkedIn URL')
    .max(120, 'LinkedIn URL cannot exceed 120 characters')
    .regex(
      /^https:\/\/([a-z]{2,3}\.)?linkedin\.com\/.*$/,
      'LinkedIn URL must be a valid LinkedIn profile link',
    )
    .optional()
    .or(z.literal('')),
  agreeToTerms: z
    .boolean()
    .refine((value) => value === true, {
      message: 'You must agree to the terms and conditions',
    })
    .optional(),
  tour_state: z
    .object({
      free_user_tour_state: z.string().nullable().optional(),
      subscribed_user_tour_state: z.string().nullable().optional(),
    })
    .optional(),
  last_scan_initiated_at: z.string().nullable().optional(),
  metadata: z.object({
    dob_counter: z.number().nullable().optional(),
    todo_actions: z.array(z.union([
      z.object({
        action_category: z.literal('broker'),
        broker_name: z.string(),
        action: z.enum(['removal', 'confirmator', 'later']),
      }),
      z.object({
        action_category: z.literal('data_comm_acknowledgement'),
        action: z.enum(['acknowledge', 'later']),
        completed_at: z.string().nullable().optional(),
      })
    ])).optional().default([]),
    getting_started_completed: z.boolean().nullable().optional(),  
    onboarding_step: z.number().nullable().optional(),
  }).optional(),
  created_at: z.string().nullable().optional(),
})

export const asProfilePost = (
  schema: z.TypeOf<typeof profileSchema>,
): ProfileFields => ({
  names: {
    first: schema.firstName.map(({ value }) => value).filter(Boolean),
    middle: schema.middleName.map(({ value }) => value),
    last: schema.lastName.map(({ value }) => value).filter(Boolean),
  },
  emails: schema.email
    .map(({ value }) => value)
    .filter(Boolean)
    .map((address) => ({ address, validated: false })),
  birthdate: schema.birthdate ? schema.birthdate.toISOString() : '',
  phones: schema.phoneNumber
    .map(({ value }) => value)
    .filter(Boolean)
    .map((number) => ({ validated: false, number })),
  address: schema.address.map(({ streetAddress, ...rest }) => ({
    street1: streetAddress,
    country: 'US',
    ...rest,
  })),
  gender: schema.gender,
  mp_auth_letter_signature: schema.mp_auth_letter_signature,
  auth_letter_s3_key: schema.auth_letter_s3_key,
  auth_letter_version: schema.auth_letter_version,
  linkedIn: schema.linkedIn,
  additional_information: {
    additional_first_name: schema.otherFirstName
      ? schema.otherFirstName
          .map(({ value }) => value)
          .filter((val): val is string => val !== undefined && val !== '')
      : [],
    maiden_name: schema.otherMaidenName
      ? schema.otherMaidenName
          .map(({ value }) => value)
          .filter((val): val is string => val !== undefined && val !== '')
      : [],
  },
  tour_state: schema.tour_state,
  last_scan_initiated_at: schema.last_scan_initiated_at || undefined,
  metadata: (schema.metadata || { dob_counter: 0, todo_actions: [], getting_started_completed: false }) as any,
})

export const fromProfileFields = (
  fields: ProfileFields,
): z.TypeOf<typeof profileSchema> => ({
  firstName:
    fields.names.first.length > 0
      ? fields.names.first.map((value) => ({ value }))
      : [{ value: '' }],
  lastName:
    fields.names.last.length > 0
      ? fields.names.last.map((value) => ({ value }))
      : [{ value: '' }],
  middleName:
    fields.names.middle.length > 0
      ? fields.names.middle.map((value: any) => ({ value }))
      : [{ value: '' }],
  otherFirstName:
    fields.additional_information &&
    fields.additional_information.additional_first_name &&
    fields.additional_information.additional_first_name.length > 0
      ? fields.additional_information.additional_first_name.map(
          (value: string) => ({ value }),
        )
      : [{ value: '' }],
  otherMaidenName:
    fields.additional_information &&
    fields.additional_information.maiden_name &&
    fields.additional_information.maiden_name.length > 0
      ? fields.additional_information.maiden_name.map((value: string) => ({
          value,
        }))
      : [{ value: '' }],
  email: fields.emails.map(({ address }) => ({ value: address })),
  phoneNumber: fields.phones.map(({ number }) => {
    let val = number || '';
    if (val && !val.startsWith('+')) {
      val = val.length === 10 ? `+1${val}` : `+${val}`;
    }
    return { value: val };
  }),
  birthdate: (fields.birthdate ? new Date(fields.birthdate) : null) as any,
  // below is mad because it thinks we can't convert a string to state
  // @TODO fix the type ignore
  // @ts-ignore
  address:
    fields.address.length > 0
      ? fields.address.map(({ city, state, street1: streetAddress, zip }) => ({
          city,
          state,
          streetAddress: streetAddress ?? '',
          zip: zip ?? '',
        }))
      : [{ city: '', state: '', streetAddress: '', zip: '' }],
  mp_auth_letter_signature: fields.mp_auth_letter_signature || '',
  auth_letter_s3_key: fields.auth_letter_s3_key || '',
  gender: fields.gender || '',
  auth_letter_version:
    fields.auth_letter_version || CURRENT_AUTH_LETTER_VERSION,
  linkedIn: fields.linkedIn,
  tour_state: fields.tour_state,
  last_scan_initiated_at: fields.last_scan_initiated_at,
  metadata: fields.metadata,
  created_at: fields.created_at,
})

const step1AddressSchema = z.object({
  city: z.string().nonempty('City is required').max(100, 'City cannot be longer than 100 characters'),
  state: z.enum(states, {
    errorMap: (issue, ctx) => {
      if (!ctx.data || ctx.data === '') return { message: 'State is required' }
      return { message: 'Invalid state code (e.g. NY)' }
    },
  }),
}).array()

export const createStep1Schema = () =>
  z.object({
    firstName: z
      .object({
        value: z
          .string()
          .min(1, 'First name is required')
          .max(50, 'Name cannot be more than 50 characters')
          .regex(/^[a-zA-Z]+(?:[\s-][a-zA-Z]+)*$/, 'First name can only contain letters and hyphens (-)'),
      })
      .array(),
    middleName: z
      .object({
        value: z
          .string()
          .max(50, 'Name cannot be more than 50 characters')
          .regex(/^(?:[a-zA-Z]+(?:[\s-][a-zA-Z]+)*)?$/, 'Middle name can only contain letters and hyphens (-)')
          .refine((v) => v.length > 0, { message: 'Middle name is required' }),
      })
      .array(),
    lastName: z
      .object({
        value: z
          .string()
          .min(1, 'Last name is required')
          .max(50, 'Name cannot be more than 50 characters')
          .regex(/^[a-zA-Z]+(?:[\s-][a-zA-Z]+)*$/, 'Last name can only contain letters and hyphens (-)'),
      })
      .array(),
    birthdate: z.coerce
      .date()
      .nullable()
      .refine((date) => date !== null && !isNaN(date.getTime()), { message: 'Date of birth is required' })
      .refine((date) => date && date <= timeAgo({ years: 18 }), { message: 'You must be at least 18 years old to use Priwall' })
      .refine((date) => date && date >= timeAgo({ years: 100 }), { message: 'You must be under 100 years old to use Priwall' }),
    address: step1AddressSchema,
  })

export type Step1SchemaType = z.TypeOf<ReturnType<typeof createStep1Schema>>

export const fromStep1Fields = (fields: ProfileFields): Step1SchemaType => ({
  firstName: [{ value: fields.names.first[0] ?? '' }],
  middleName: [{ value: fields.names.middle[0] ?? '' }],
  lastName: [{ value: fields.names.last[0] ?? '' }],
  birthdate: fields.birthdate ? new Date(fields.birthdate) : null,
  // @ts-ignore
  address:
    fields.address.length > 0
      ? [{ city: fields.address[0].city, state: fields.address[0].state }]
      : [{ city: '', state: '' as 'CA' }],
})

export const asStep1ProfilePost = (schema: Step1SchemaType, overwrite = false) => ({
  names: {
    first: schema.firstName.map(({ value }) => value).filter(Boolean),
    middle: schema.middleName.map(({ value }) => value),
    last: schema.lastName.map(({ value }) => value).filter(Boolean),
  },
  name: {
    first: schema.firstName[0]?.value?.trim() ?? '',
    middle: schema.middleName[0]?.value?.trim() ?? '',
    last: schema.lastName[0]?.value?.trim() ?? '',
  },
  birthdate: schema.birthdate ? schema.birthdate.toISOString() : '',
  address: schema.address.map(({ city, state }) => ({
    city,
    state,
    street1: '',
    zip: '',
    country: 'US',
  })),
  overwrite,
})

export const step1EmptySchema = (): Step1SchemaType => ({
  firstName: [{ value: '' }],
  middleName: [{ value: '' }],
  lastName: [{ value: '' }],
  birthdate: null,
  address: [{ city: '', state: '' as 'CA' }],
})

export const emptyProfileSchema = (): z.TypeOf<typeof profileSchema> => ({
  firstName: [{ value: '' }],
  middleName: [{ value: '' }],
  lastName: [{ value: '' }],
  otherFirstName: [{ value: '' }],
  otherMaidenName: [{ value: '' }],
  address: [
    {
      city: '',
      state: '' as 'CA',
      streetAddress: '',
      zip: '',
    },
  ],
  email: [{ value: '' }],
  phoneNumber: [{ value: '' }],
  birthdate: new Date(),
  mp_auth_letter_signature: '',
  auth_letter_s3_key: '',
  gender: '',
  auth_letter_version: CURRENT_AUTH_LETTER_VERSION,
  linkedIn: '',
  agreeToTerms: false,
  metadata: { dob_counter: 0, todo_actions: [], getting_started_completed: false },
})